import React, {useEffect, useRef} from 'react';
import {
    APIProvider,
    Map,
    useMap,
} from '@vis.gl/react-google-maps';
import ApplicationProvider from "pages/application-provider";
import NavigationLayout from "pages/navigation-layout";
import {Person} from "lib/types";
import MapFilters from "components/heatmap/MapFilters";

interface Props {
    isAuthenticated: string;
    csrfToken: string;
    logoutUrl: string;
    userFirstname: string;
    userLastname: string;
    centreX: string;
    centreY: string;
    zoom: string;
    olderPersons: string;
    volunteers: string;
    chns: string;
    rhaTeams: string;
    rha: string;
    chn: string;
    gender: string;
    userDefaultRha: string;
    userDefaultChn: string;
    googleMapsKey: string;
    googleMapId: string;
    staticBaseUrl: string;
}

function getMetersPerPixel(zoom: number): number {
    return 64000 / Math.pow(2, zoom);
}

function MapCirclesAndHeatmap({
                                  olderPersonsJson,
                                  volunteersJson,
                              }: {
    olderPersonsJson: string;
    volunteersJson: string;
}) {
    const olderPersons = JSON.parse(olderPersonsJson);
    const volunteers = JSON.parse(volunteersJson);

    console.log(olderPersons)

    const map = useMap();
    const circlesRef = useRef<google.maps.Circle[]>([]);

    useEffect(() => {
        if (!map) return;
        circlesRef.current.forEach((circle) => circle.setMap(null));
        circlesRef.current = [];

        const basePixelRadius = 4;
        const zoom = map.getZoom() ?? 10;

        olderPersons.forEach((loc: Person) => {
            const circle = new google.maps.Circle({
                strokeColor: "#FF0000",
                strokeOpacity: 0.35,
                strokeWeight: 0,
                fillColor: "#FF0000",
                fillOpacity: 0.7,
                map,
                center: {lat: loc.fields.latitude, lng: loc.fields.longitude},
                radius: basePixelRadius * getMetersPerPixel(zoom),
            });
            circlesRef.current.push(circle);
        });

        const heatmap = new google.maps.visualization.HeatmapLayer({
            data: volunteers.map((v: Person) => new google.maps.LatLng(v.fields.latitude, v.fields.longitude)),
            radius: 20,
            gradient: [
                'rgba(214, 214, 255, 0)',
                'rgba(214, 214, 255, 1)',
                'rgba(180, 180, 255, 1)',
                'rgba(150, 150, 255, 1)',
                'rgba(120, 120, 255, 1)',
                'rgba(90, 90, 255, 1)',
                'rgba(60, 60, 255, 1)',
                'rgba(40, 60, 255, 1)',
                'rgba(20, 30, 255, 1)',
                'rgba(0, 0, 255, 1)',
            ],
            map,
        });

        const zoomListener = map.addListener('zoom_changed', () => {
            const newZoom = map.getZoom() ?? 10;
            circlesRef.current.forEach((circle) => {
                circle.setRadius(basePixelRadius * getMetersPerPixel(newZoom));
            });
        });

        return () => {
            heatmap.setMap(null);
            google.maps.event.removeListener(zoomListener);
            circlesRef.current.forEach((circle) => circle.setMap(null));
        };
    }, [map, olderPersons, volunteers]);

    return null;
}

export default function MapHeatmapPage(props: Props) {
    const chnsArray = JSON.parse(props.chns || '[]');
    const rhaTeamsArray = JSON.parse(props.rhaTeams || '[]');
    return (
        <ApplicationProvider>
            <NavigationLayout {...props} mainContainerColor="#F4F4F4">
                <APIProvider
                    apiKey={props.googleMapsKey}
                    libraries={['visualization']}
                    version="3.64"
                >
                    <div style={{position: 'relative', height: 'calc(100svh - 96px)'}}>
                        <MapFilters
                            chns={chnsArray}
                            rhaTeams={rhaTeamsArray}
                            rha={props.rha}
                            chn={props.chn}
                            gender={props.gender}
                            userDefaultRha={props.userDefaultRha}
                            userDefaultChn={props.userDefaultChn}
                        />
                        <Map
                            defaultCenter={{lat: parseFloat(props.centreY), lng: parseFloat(props.centreX)}}
                            defaultZoom={parseFloat(props.zoom)}
                            mapId={props.googleMapId}
                            gestureHandling="greedy"
                            disableDefaultUI={true}
                            // style={{width: "calc(100svw - 300px)", height: "calc(100svh - 96px)"}}
                        >
                            <MapCirclesAndHeatmap
                                olderPersonsJson={props.olderPersons}
                                volunteersJson={props.volunteers}
                            />
                        </Map>
                    </div>
                </APIProvider>
            </NavigationLayout>
        </ApplicationProvider>
    );
}